import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TruncatedIcosahedron from './TruncatedIcosahedron';

export default function IntroAnimation({ onComplete }) {
  const [phase, setPhase] = useState('start'); // 'start', 'trail', 'portal', 'complete'
  const [showIcosahedron] = useState(true);

  useEffect(() => {
    // Phase transitions - animazione di 3 secondi
    const timer1 = setTimeout(() => setPhase('trail'), 300);
    const timer2 = setTimeout(() => setPhase('portal'), 1500);
    const timer3 = setTimeout(() => setPhase('complete'), 3500);
    const timer4 = setTimeout(() => onComplete(), 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase !== 'complete' && (
        <motion.div
          className="fixed inset-0 z-50 bg-gradient-to-b from-[#0a1628] via-[#1a365d] to-[#0a1628] overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Icosahedron Portal in center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[400px] h-[400px] md:w-[500px] md:h-[500px]">
              <TruncatedIcosahedron visible={showIcosahedron} showPortalEffect={phase === 'portal'} />
            </div>
          </div>

          {/* Snow trail particles - moving from left */}
          {(phase === 'trail' || phase === 'portal') && (
            <>
              {/* Main luminous trail */}
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 h-1 left-0"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 20%, white 50%, rgba(255,255,255,0.3) 80%, transparent 100%)',
                  boxShadow: '0 0 30px 10px rgba(255,255,255,0.6), 0 0 60px 20px rgba(255,255,255,0.4), 0 0 100px 40px rgba(135,206,235,0.3)',
                  width: '40%',
                }}
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ 
                  x: ['0%', '150%'],
                  opacity: [0, 1, 1, 0.8, 0],
                }}
                transition={{ 
                  duration: 3,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              />

              {/* Trailing particles */}
              {[...Array(30)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: Math.random() * 6 + 2,
                    height: Math.random() * 6 + 2,
                    top: `calc(50% + ${(Math.random() - 0.5) * 100}px)`,
                    boxShadow: '0 0 10px 3px rgba(255,255,255,0.8), 0 0 20px 6px rgba(135,206,235,0.5)',
                  }}
                  initial={{ 
                    x: -50,
                    opacity: 0,
                  }}
                  animate={{ 
                    x: ['-5vw', '110vw'],
                    opacity: [0, 1, 1, 0],
                    y: [(Math.random() - 0.5) * 30, (Math.random() - 0.5) * 50],
                  }}
                  transition={{ 
                    duration: 3.2 + Math.random() * 0.5,
                    delay: i * 0.05,
                    ease: 'easeOut',
                  }}
                />
              ))}

              {/* Secondary glow trail */}
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 h-3"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.8) 0%, rgba(135,206,235,0.4) 40%, transparent 70%)',
                  filter: 'blur(4px)',
                  width: '30%',
                }}
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ 
                  x: ['0%', '160%'],
                  opacity: [0, 0.8, 0.8, 0],
                }}
                transition={{ 
                  duration: 3.1,
                  delay: 0.1,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              />
            </>
          )}

          {/* Flash effect when trail passes through portal */}
          <motion.div
            className="absolute inset-0 bg-white pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: phase === 'portal' ? [0, 0.3, 0] : 0,
            }}
            transition={{ 
              duration: 0.6,
              delay: 1.2,
            }}
          />

          {/* Text appears after portal effect */}
          <motion.div
            className="absolute bottom-16 md:bottom-20 left-0 right-0 text-center z-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: phase === 'portal' ? 1 : 0,
              y: phase === 'portal' ? 0 : 30,
            }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <h1 className="text-3xl md:text-5xl font-extralight text-white tracking-[0.4em] mb-3">
              ZIANO DI FIEMME
            </h1>
            <p className="text-sm md:text-base text-blue-200/80 tracking-[0.25em] uppercase font-light">
              Entra nel mondo degli atleti
            </p>
          </motion.div>

          {/* Skip button */}
          <motion.button
            className="absolute bottom-6 right-6 text-white/40 text-xs tracking-wider hover:text-white/70 transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            onClick={onComplete}
          >
            SALTA INTRO
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}